import {
  Button,
  CascaderField,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  FieldLabel,
  Form,
  type OperationType,
  RemovableCard,
  Row,
  SelectField,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { SelectMinistryField } from '@/features/master-table/components';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import {
  ImplementationMethodLabelModifier,
  ProjectCategoryLabelModifier,
  implementationMethodEnum,
  projectCategoryEnum,
} from '@/utils/enum';
import { PlusOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';
import type { RsFormSchema } from '../../_model/project-form';
import { handleProjectCategoryChange } from '../../_utils/project-form-handler';
import { FiscalYearField } from '../fields/FiscalYearField';
import { LegalBasesArrayField } from '../fields/LegalBasesArrayField';
import { MainCostField } from '../fields/MainCostField';
import { OtherResponsibleOrganizationsArrayField } from '../fields/OtherResponsibleOrganizationsArrayField';
import { PeriodAndStartEndCheckField } from '../fields/PeriodAndStartEndCheckField';
import { PlansArrayField } from '../fields/PlansArrayField';
import { PoliciesArrayField } from '../fields/PoliciesArrayField';
import { RelatedOrganizationArrayField } from '../fields/RelatedOrganizationArrayField';
import { SnapshotField } from '../fields/SnapshotField';
import { SortOrderField } from '../fields/SortOrderField';
import { RelatedProjectArrayField } from '../fields/related-project-array-field/RelatedProjectArrayField';

type FormValues = z.infer<typeof RsFormSchema>;
type InitialFormValues = {
  data: Partial<FormValues['data']>;
};

type Props = {
  operation: OperationType;
  ignoredIdsForRelatedProject?: string[];
};

export const RsProjectForm: FC<Props> = ({
  operation,
  ignoredIdsForRelatedProject,
}) => {
  const { t } = useTranslation();
  const {
    watch,
    control,
    setValue,
    formState: { errors },
    resetField,
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const [fiscalYear, implementationMethods, fiscalYearOfSnapshot] = watch([
    'data.fiscalYear',
    'data.implementationMethods',
    'data.fiscalYearOfSnapshot',
  ]);

  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  const {
    fields: subsidyRateFields,
    append: appendSubsidyRate,
    remove: removeSubsidyRate,
  } = useFieldArray({
    control,
    name: 'data.subsidyRates',
  });

  return (
    <>
      <ContentWrapper paddingTop={0}>
        <ContentItem borderBottom="0" paddingBottom={0} paddingTop={0}>
          <ContentItems
            items={[
              <FiscalYearField
                key="data.fiscalYear"
                operation={operation}
                onChange={() => {
                  resetField('data.mainCostIds', { defaultValue: [] });
                }}
              />,
              <SortOrderField
                key="data.sortOrder"
                hint={t('hint-project-order-number')}
              />,
              <SnapshotField key="data.snapshotId" />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('organization')}>
        <ContentItem>
          <ContentItems
            mode="fillRight"
            items={[
              <SelectMinistryField
                key="data.ministryId"
                control={control}
                errors={errors}
                name="data.ministryId"
              />,
            ]}
          />
        </ContentItem>

        <ContentItem>
          <ContentItems
            items={[
              <CascaderField
                key="data.organizationIds"
                name="data.organizationIds"
                label={t('organization-in-charge')}
                hint={t('hint-organization-in-charge-for-rs')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                enableIntermediate
                disabled={!fiscalYear}
                required
                loading={organizationOptions.isPending}
                textNotInOption={t('not-exist-in-organization-options')}
                useNotInOption
              />,
            ]}
          />

          <ContentItems
            items={[
              <UserSelectField
                key="data.peopleInChargeIds"
                name="data.peopleInChargeIds"
                label={t('people-in-charge')}
                hint={t('hint-people-in-charge-for-rs')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                useNotInOption
                snapshotId={snapshotId ?? undefined}
                required
              />,
              <UserSelectField
                useNotInOption
                key="data.businessManagerIds"
                name="data.businessManagerIds"
                mode="multiple"
                label={t('business-manager')}
                hint={t('hint-business-manager-for-rs')}
                placeholder={t('placeholder-business-manager')}
                control={control}
                errors={errors}
                organizationFilter="modal"
                snapshotId={snapshotId ?? undefined}
                required
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom="0" paddingBottom={0}>
          <OtherResponsibleOrganizationsArrayField type="RS" />
          <RelatedOrganizationArrayField type="RS" required />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('basic-information')}>
        <ContentItem>
          <ContentItems
            items={[
              <TextInputField
                key="data.name"
                name="data.name"
                autocompleteKey="rsProject.name"
                label={t('project-name')}
                control={control}
                errors={errors}
                required
              />,
            ]}
          />

          <ContentItems
            mode="fillRight"
            items={[
              <SelectField
                key="data.category"
                name="data.category"
                label={t('project-category')}
                hint={t('hint-project-category')}
                control={control}
                errors={errors}
                options={projectCategoryEnum.options.map((value) => ({
                  value,
                  label: ProjectCategoryLabelModifier[value],
                }))}
                required
                shouldCoexistOnChange
                onChange={(value) => {
                  handleProjectCategoryChange(
                    value,
                    fiscalYear,
                    setValue,
                    resetField,
                  );
                }}
              />,
            ]}
          />

          <ContentItems
            mode="fullLine"
            items={[
              <PeriodAndStartEndCheckField
                key="data.period-data.isUnknownStart-data.isNoScheduledEnd"
                hint={t('hint-project-start-end-period')}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom="0" paddingBottom={0}>
          <PoliciesArrayField />
          <ContentItems
            items={[
              <MainCostField
                key="data.mainCostIds"
                fiscalYear={fiscalYear?.year().toString()}
              />,
            ]}
          />
          <RelatedProjectArrayField
            operation={operation}
            type="RS"
            ignoredProjectIds={ignoredIdsForRelatedProject}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <TextAreaInputField
                key="data.purpose"
                name="data.purpose"
                label={t('project-purpose')}
                hint={t('hint-project-purpose')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <TextAreaInputField
                key="data.task"
                name="data.task"
                label={t('project-task')}
                hint={t('hint-project-task-for-rs')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
            ]}
          />

          <ContentItems
            items={[
              <TextAreaInputField
                key="data.overview"
                name="data.overview"
                label={t('project-overview')}
                hint={t('hint-project-overview-for-rs')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <TextInputField
                key="data.overviewUrl"
                name="data.overviewUrl"
                label={t('overview-url')}
                hint={t('hint-project-overview-url')}
                control={control}
                errors={errors}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('legal-bases')}>
        <ContentItem>
          <LegalBasesArrayField />
        </ContentItem>

        <ContentItem borderBottom="0" paddingBottom={0}>
          <Form.Item
            label={
              <FieldLabel
                text={t('project-plan')}
                hint={t('hint-related-plans')}
              />
            }
          >
            <PlansArrayField autocompleteKey="rsProject.plan.url" />
          </Form.Item>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('implementation-method-and-subsidy-rate-and-so-on')}
      >
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <SelectField
                key="data.implementationMethods"
                name="data.implementationMethods"
                label={t('implementation-method')}
                hint={t('hint-implementation-method')}
                control={control}
                errors={errors}
                options={implementationMethodEnum.options.map((value) => ({
                  value,
                  label: ImplementationMethodLabelModifier[value],
                }))}
                mode="multiple"
                required
                shouldCoexistOnChange
                onChange={(value) => {
                  if (
                    value.includes('subsidy') &&
                    subsidyRateFields.length === 0
                  ) {
                    appendSubsidyRate({
                      target: null,
                      ratesText: null,
                      limitText: null,
                      url: null,
                    });
                  }
                }}
              />,
            ]}
          />
          <Form.Item
            label={
              <FieldLabel
                text={t('subsidy-rate-and-so-on')}
                hint={t('hint-subsidy-rate')}
              />
            }
          >
            <Row gutter={[24, 8]}>
              {subsidyRateFields.map((item, idx) => {
                const requiredSubsidyRate =
                  implementationMethods?.includes('subsidy');
                return (
                  <Col key={item.id} span={24}>
                    <RemovableCard
                      hideCloseButton={
                        subsidyRateFields.length <= 1 && requiredSubsidyRate
                      }
                      onRemove={() => {
                        removeSubsidyRate(idx);
                      }}
                    >
                      <Row gutter={[24, 8]}>
                        <Col span={12}>
                          <TextAreaInputField
                            name={`data.subsidyRates.${idx}.target`}
                            label={t('subsidy-rate-target')}
                            control={control}
                            errors={errors}
                            required={requiredSubsidyRate}
                          />
                        </Col>
                        <Col span={12}>
                          <TextAreaInputField
                            name={`data.subsidyRates.${idx}.ratesText`}
                            label={t('subsidy-rate')}
                            control={control}
                            errors={errors}
                            required={requiredSubsidyRate}
                          />
                        </Col>
                        <Col span={12}>
                          <TextAreaInputField
                            name={`data.subsidyRates.${idx}.limitText`}
                            label={t('subsidy-rate-limit-and-so-on')}
                            control={control}
                            errors={errors}
                            required={requiredSubsidyRate}
                          />
                        </Col>
                        <Col span={12}>
                          <TextInputField
                            name={`data.subsidyRates.${idx}.url`}
                            autocompleteKey="rsProject.subsidyRate.url"
                            label={t('subsidy-rate-url')}
                            control={control}
                            errors={errors}
                          />
                        </Col>
                      </Row>
                    </RemovableCard>
                  </Col>
                );
              })}
              <Col span={12}>
                <Button
                  type="dashed"
                  onClick={() => {
                    appendSubsidyRate({
                      target: null,
                      ratesText: null,
                      limitText: null,
                      url: null,
                    });
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('add-subsidy-rate')}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('others')}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <TextInputField
                key="data.oldProjectNumber"
                name="data.oldProjectNumber"
                label={t('old-project-number')}
                hint={t('hint-old-project-number-for-rs')}
                control={control}
                errors={errors}
              />,
              <TextAreaInputField
                key="data.remark"
                name="data.remark"
                label={t('remark')}
                hint={t('hint-project-remark')}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
